import { action, computed, observable } from 'mobx';
import {observer, Provider} from 'mobx-react';
import {
    addUnitsOfTime,
    autoinject,
    bind,
    DateRangePicker,
    displayFormatTime,
    Duration,
    Enums,
    formatDuration,
    FormStore,
    getDurationToNow,
    HelpTooltip,
    inject, Modal, ModalHandle,
    NothingHere,
    slugCase,
    SubTopBar, subUnitsOfTime,
    TasksStore,
} from 'Mri.Web.Common';
import { stringify } from 'qs';
import * as React from 'react';
import { BsFillGridFill } from '@react-icons/all-files/bs/BsFillGridFill';
import { BsListUl } from '@react-icons/all-files/bs/BsListUl';
import { BsTv } from '@react-icons/all-files/bs/BsTv';
import { RouteComponentProps } from 'react-router';
import { LinkContainer } from 'react-router-bootstrap';
import { DropdownItem, DropdownMenu, DropdownToggle, Form, UncontrolledButtonDropdown } from 'reactstrap';
import { AppStore } from '../../Core/AppStore';
import { DisplayEventsList } from '../DisplayEventsList';
import { DisplaysStore } from '../DisplaysStore';
import displayName = Provider.displayName;
import EventStatusChangeLog = Mri.Audit.EventStatusChangeLog;
import {format} from "date-fns-tz";

export interface IDisplayEventLogParams {
    serialNumber: string;
}
export interface IDisplayEventLogListProps extends RouteComponentProps<IDisplayEventLogParams> {}

@autoinject
@observer
export class DisplayEventLogList extends React.Component<IDisplayEventLogListProps, any> {
    public constructor(props: IDisplayEventLogListProps) {
        super(props);
        this.state = {};

        const startTime = subUnitsOfTime(new Date(), 1, 'day');
        const endTime = addUnitsOfTime(new Date(), 1, 'day');
        
        this.formStore.fields.startTime.setValue(startTime);
        this.formStore.fields.endTime.setValue(endTime);
    }

    @action
    public async componentDidMount() {
        this.loading = true;

        try {
            await this.displaysStore.loadAsync(this.props.match.params.serialNumber);
            await this.getEventLogAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    public render() {
        return (
            <div>
                <SubTopBar>
                    <Form onSubmit={this.onSubmit}>
                        <div className="d-block w-100">
                            <DateRangePicker
                                startDate={this.formStore.fields.startTime}
                                endDate={this.formStore.fields.endTime}
                                className="d-inline-block me-2"
                                timezone={this.displaysStore.timezones.get(this.props.match.params.serialNumber)}
                            />

                            <button className="mx-2 btn btn-primary">{this.loading ? 'Loading...' : 'Apply'}</button>

                            <a className="btn btn-link" onClick={this.exportCsvUrlAsync}>
                                Export to CSV
                            </a>
                        </div>
                    </Form>

                    <UncontrolledButtonDropdown className="ms-auto">
                        <DropdownToggle caret size="lg" color="link">
                            <BsListUl className={''} />
                        </DropdownToggle>

                        <DropdownMenu end>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/event-log/cards`}>
                                <DropdownItem>
                                    <BsFillGridFill /> Card View
                                </DropdownItem>
                            </LinkContainer>
                            <LinkContainer to={`/displays/${this.props.match.params.serialNumber}/event-log/list`}>
                                <DropdownItem>
                                    <BsListUl className="" /> List View
                                </DropdownItem>
                            </LinkContainer>
                        </DropdownMenu>
                    </UncontrolledButtonDropdown>
                </SubTopBar>

                <div>
                    <table className="table table-striped fixed-header">
                        <thead>
                            <tr>
                                <th>Details</th>
                                <th>Duration</th>
                                <th>Start Time</th>
                                <th>Type</th>
                                <th>Active</th>
                                <th>Status</th>
                                <th>Log</th>
                            </tr>
                        </thead>
                        <tbody>{this.displayEvents && this.displayEvents.map(e => <EventLogListRow serialNumber={this.props.match.params.serialNumber} displayEvent={e}/>)}</tbody>
                    </table>
                    <NothingHere
                        empty={!this.displayEvents?.length}
                        icon={<BsTv />}
                        loading={this.loading}
                        label="No Display Events"
                        loadingMessage="Loading Events..."
                    />
                </div>
            </div>
        );
    }
    
    @bind
    private onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        this.getEventLogAsync();
    }

    @bind
    @action
    private async getEventLogAsync() {
        this.loading = true;
        let startTime = new Date(this.formStore.fields.startTime.value);
        let endTime = new Date(this.formStore.fields.endTime.value);
        let oneYearAgo = subUnitsOfTime(new Date(), 1, 'years');
        let dayInFuture = addUnitsOfTime(new Date(), 1, 'day');

        if (startTime < oneYearAgo) startTime = subUnitsOfTime(new Date(), 1, 'day');
        if(endTime > dayInFuture) endTime = new Date();
        
        try {
            const events = await this.displaysStore.getEventHistoryAsync(
                this.props.match.params.serialNumber,
                startTime,
                endTime
            );
            if (events) this.displayEvents = events;
        } catch (e: any) {
            this.appStore.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    private async exportCsvUrlAsync() {
        const query = stringify({
            startTime: this.formStore.values.startTime,
            endTime: this.formStore.values.endTime,
        });
        const task = this.tasksStore.startSessionTask('Export Display Events');

        try {
            await this.displaysStore.exportEventLogAsync(this.props.match.params.serialNumber, query);
            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loading = false;
    @observable
    private displayEvents?: Mri.DisplayEvents.DisplayEvent[];
    @inject
    private appStore!: AppStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private tasksStore!: TasksStore;
}

export interface IEventLogLisRowProps {
    readonly serialNumber: string;
    readonly displayEvent: Mri.DisplayEvents.DisplayEvent;
}

@autoinject
@observer
export class EventLogListRow extends React.Component<IEventLogLisRowProps> {
    public render(){
        const {displayEvent} = this.props;
        const startTime = displayEvent.downtimeIncidentStartTime ?? displayEvent.creationTime;

        const duration =
            displayEvent.downtimeIncidentDuration != null
                ? new Duration(displayEvent.downtimeIncidentDuration * 1000)
                : displayEvent.downtimeActiveIncident
                    ? getDurationToNow(startTime)
                    : undefined;
        return (
            <tr>
                <td>
                    <HelpTooltip
                        subjects={['Downtime Incidents']}
                        name={slugCase(Enums.toString(Enums.DowntimeIncidentType, displayEvent.downtimeIncidentType))}
                        underline
                        placement="top"
                        className={this.eventSeverityStyle(displayEvent)}
                    >
                        <b className={this.eventSeverityStyle(displayEvent)}>{displayEvent.details}</b>
                    </HelpTooltip>
                </td>
                <td>{duration != null ? formatDuration(duration) : '-'}</td>
                <td>
                    {displayFormatTime(
                        startTime,
                        'EEEE, MMMM do yyyy, h:mm:ss a zzz',
                        this.props.serialNumber
                    )}
                </td>
                <td>
                    <div className={this.incidentClass(displayEvent) + ' me-1'}>
                        {Enums.toString(Enums.DisplayIncidentLevel, displayEvent.incidentLevel)}
                    </div>
                </td>
                <td>
                    <div>
                        {displayEvent.downtimeActiveIncident ? "Active" : "Inactive"}
                    </div>
                </td>
                <td>
                    <div className={this.badgeClass(displayEvent)}>
                        {Enums.toString(Enums.DisplayEventStatus, displayEvent.displayEventStatus)}
                    </div>
                </td>
                <td>
                    <button className="btn btn-link" onClick={() => this.viewLog(displayEvent.displayEventGuid)}>
                        View Log
                    </button>

                    <Modal
                        className="modal-lg"
                        title={displayEvent.details + " log"}
                        modalHandle={this.modalHandle}
                        btnClass="w-50"
                        footerClass="d-none"
                    >
                        <div>
                            <table className="table table-sm">
                                <thead>
                                <tr>
                                    <th scope="col">Event</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">Profile</th>
                                    <th scope="col">Updated Event Status</th>
                                    <th scope="col">Date</th>
                                </tr>
                                </thead>
                                <tbody>
                                {this.displayEventChangeLog?.map(this.renderEventLog)}
                                </tbody>
                            </table>

                            <NothingHere
                                empty={!this.displayEventChangeLog?.length}
                                icon={<BsTv/>}
                                loading={this.loadingLog}
                                label="No Status Changes"
                                loadingMessage="Loading Log..."
                            />
                        </div>
                    </Modal>
                </td>
            </tr>
        );
    }

    public renderEventLog(auditLog: Mri.Audit.AuditLog<any>, key: any) {
        const log = auditLog.payload as Mri.Audit.EventStatusChangeLog;
        const url = log.profileGuid ? '/display-event-profiles/automations/' + log.profileGuid + '/edit' : undefined;

        return (
            <tr key={key}>
                <td scope="row">{log.eventDetails}</td>
                <td scope="row">{log.email}</td>
                <td scope="row">
                    {url && log.profileName != "Quick Clear" &&
                        <a href={url} target="_blank" className="p-0 btn btn-link">
                            {log.profileName}
                        </a>
                    }
                    {url && log.profileName == "Quick Clear" &&
                        <span>
                            {log.profileName}
                        </span>
                    }
                </td>
                <td scope="row">{Enums.toString(Enums.DisplayEventStatus, log.eventStatus)}</td>
                <td>{format(auditLog.timestamp, 'Pp z')}</td>
            </tr>
        );
    }

    @bind
    @action
    private async viewLog(eventGuid: string) {
        this.modalHandle.open();
        this.loadingLog = true;
        try {
            this.displayEventChangeLog = await this.displaysStore.loadEventStatusChangeLogAsync(this.props.serialNumber, eventGuid);
        } catch{
            console.error('Error fetching event log', eventGuid);
        }
        this.loadingLog = false;
    }

    @bind
    @action
    private badgeClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';
        if (displayEvent.displayEventStatus == Mri.DisplayEvents.DisplayEventStatus.Alert) {
            return badge + 'bg-warning';
        }
        return badge + 'bg-secondary';
    }

    @bind
    @action
    private incidentClass(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        const badge = 'badge ';

        let color = '';

        switch (displayEvent.incidentLevel) {
            default:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                color = 'bg-danger';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                color = 'bg-warning';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                color = 'bg-teal';
                break;
            case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                color = 'bg-secondary';
                break;
        }

        return badge + color;
    }

    @bind
    @action
    private eventSeverityStyle(displayEvent: Mri.DisplayEvents.DisplayEvent) {
        let color = '';
        if (displayEvent.downtimeActiveIncident) {
            switch (displayEvent.incidentLevel) {
                default:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentCritical:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMajor:
                    color = 'text-danger';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentMinor:
                    color = 'text-warning';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentNotification:
                    color = 'text-teal';
                    break;
                case Mri.DisplayApi.IncidentLevel.IncidentIgnored:
                    color = 'text-muted';
                    break;
            }
        } else color = 'text-muted';

        return color;
    }

    private formStore = new FormStore({
        startTime: new Date(),
        endTime: new Date(),
    });

    @observable
    private loadingLog = false;
    @observable
    private displayEventChangeLog: Mri.Audit.AuditLog<EventStatusChangeLog>[] = [];
    @inject
    private displaysStore!: DisplaysStore;
    private modalHandle = new ModalHandle();
}
